import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../documentation/JobList.css';
import { toast } from 'react-toastify'; // Using react-toastify for notifications
import JobForm from '../documentation/JobForm'; 
import Select2Dropdown from '../common/Select2Dropdown';
import { ConfirmDialog } from '../common';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE_URL = '/api/documentation/jobs/'; // base for job actions
const MANIFEST_REPORT_URL = '/api/documentation/jobs/manifest-report/';
const MASTERS_API_BASE_URL = '/api/masters/';

const AUTH_HEADERS = () => ({
  'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
  'Content-Type': 'application/json'
});

const toUpper = v => (v ? String(v).toUpperCase() : '');

const flashStyleId = 'manifest-flash-style';
const pendingFlashStyleId = 'pending-accepted-flash-style';
const pendingManifestOutFlashStyleId = 'pending-manifest-out-flash-style'; // New ID for red flash
if (!document.getElementById(flashStyleId)) {
  const styleEl = document.createElement('style');
  styleEl.id = flashStyleId;
  styleEl.innerHTML = `
  @keyframes manifestFlash {
    0% { background-color: #fff8cc; }
    50% { background-color: #ffe188; }
    100% { background-color: #fff8cc; }
  }
  tr.manifest-flash { animation: manifestFlash 1.2s ease-in-out infinite; }
  `;
  document.head.appendChild(styleEl);
}

if (!document.getElementById(pendingFlashStyleId)) {
  const styleEl2 = document.createElement('style');
  styleEl2.id = pendingFlashStyleId;
  styleEl2.innerHTML = `
    @keyframes pendingAcceptedFlash {
      0%   { background-color: #fff9b3; }  
      50%  { background-color: #ffe035; }  
      100% { background-color: #fff9b3; }
    }
    tr.pending-accepted-flash { animation: pendingAcceptedFlash 1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(styleEl2);
}

if (!document.getElementById(pendingManifestOutFlashStyleId)) {
  const styleEl3 = document.createElement('style');
  styleEl3.id = pendingManifestOutFlashStyleId;
  styleEl3.innerHTML = `
    @keyframes pendingManifestOutFlash {
      0%   { background-color: #ffcccc; }  
      50%  { background-color: #ff8080; }  
      100% { background-color: #ffcccc; }
    }
    tr.manifest-out-pending-flash { animation: pendingManifestOutFlash 1.2s ease-in-out infinite; }
  `;
  document.head.appendChild(styleEl3);
}

const dutyPaidHighlightStyleId = 'duty-paid-highlight-style';
if (!document.getElementById(dutyPaidHighlightStyleId)) {
  const styleEl4 = document.createElement('style');
  styleEl4.id = dutyPaidHighlightStyleId;
  styleEl4.innerHTML = `
    @keyframes dutyPaidHighlight {
      0%   { background-color: #fff3cd; }
      92%  { background-color: #fff3cd; }
      100% { background-color: transparent; }
    }
    tr.duty-paid-highlight { animation: dutyPaidHighlight 5s ease-in-out; }
  `;
  document.head.appendChild(styleEl4);
}

const checkNotesFlashStyleId = 'check-notes-flash-style';
if (!document.getElementById(checkNotesFlashStyleId)) {
  const styleEl5 = document.createElement('style');
  styleEl5.id = checkNotesFlashStyleId;
  styleEl5.innerHTML = `
    @keyframes checkNotesFlash {
      0%, 100% { color: #d60000; }
      50%      { color: #ff9d9d; }
    }
    .check-notes-flash {
      animation: checkNotesFlash 1s ease-in-out infinite;
      font-weight: bold;
      font-size: 11px;
    }
  `;
  document.head.appendChild(styleEl5);
}

function fmtDateShort(dt) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d)) return dt;
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
}

const formatDateTimeForBackend = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, '0');
  
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // getMonth() is zero-based
  const day = pad(date.getDate());
  
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export default function ManifestReport() {
  // --- FIX: Wrap hasPermission to allow ANY aliased permission that grants manifest report viewing ---
  const { hasPermission: rawHasPermission } = usePermissions();
  const hasPermission = (module, action) => {
      // Manifest Report is located in 3 separate menus with 3 distinct keys:
      if (module === 'MANIFEST REPORT') {
          return rawHasPermission('MANIFEST REPORT', action) || 
                 rawHasPermission('PORT MANIFEST REPORT', action) || 
                 rawHasPermission('ADMIN MANIFEST REPORT', action);
      }
      return rawHasPermission(module, action);
  };
  const isPreverification = (status) => /PRE[- ]?VERIFICATION/i.test(String(status || ''));
  const [jobs, setJobs] = useState([]);
  const [idfNo, setIdfNo] = useState('');
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingDutyFor, setSavingDutyFor] = useState(null);
  const [savingComparedFor, setSavingComparedFor] = useState(null);
  const [dutyPaidDialog, setDutyPaidDialog] = useState({
    open: false,
    jobId: null,
    idfNo: '',
    dutyPaidDate: new Date().toISOString().split('T')[0],
    saving: false,
  });

  const [editingVoyageOptions, setEditingVoyageOptions] = useState([]);
  const [voyagesList, setVoyagesList] = useState([]); // all voyages from dropdown_data  
  const [icdOptions, setIcdOptions] = useState([]); // State for dynamic ICDs
  const [flashingFields, setFlashingFields] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
          open: false,
          title: '',
          message: '',
          onConfirm: null,
          onCancel: null
    });

  const [manifestDatesDialog, setManifestDatesDialog] = useState({
    open: false,
    jobId: null,
    idfNo: '',
    dischargeDate: '',
    dischargeTime: '',
    carryInEndDate: '',
    carryInEndTime: '',
    strippingDate: '',
    cargoType: '', // To check if LCL
    saving: false
  });

  // JobForm modal (view-only)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [formMode, setFormMode] = useState('view'); // 'add', 'edit', 'view'


  // editing state
  const [editingVoyageJob, setEditingVoyageJob] = useState(null); // job id being edited for voyage
  const [selectedVoyageId, setSelectedVoyageId] = useState(null);
  const [editingIcdJob, setEditingIcdJob] = useState(null);
  const [selectedIcd, setSelectedIcd] = useState('');

  // ETB/ATB edit dialog state
  const [etbAtbDialog, setEtbAtbDialog] = useState({
    open: false, voyageId: null, etb: '', atb: '', ata: '', dischargeDate: '', saving: false, error: ''
  });

  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteJobId, setNoteJobId] = useState(null);
  const [noteSubject, setNoteSubject] = useState('');
  const [noteDetails, setNoteDetails] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [flashIds, setFlashIds] = useState(new Set);
  const [pendingManifestOutIds, setPendingManifestOutIds] = useState(new Set()); // State for red flashing
  const [highlightJobId, setHighlightJobId] = useState(null);
  const pendingHighlightIdRef = useRef(null);

  useEffect(() => {
    // load dropdown data voyages once
    (async () => {
      try {
        const res = await fetch('/api/documentation/jobs/dropdown_data/', { headers: AUTH_HEADERS() });
        if (!res.ok) return;
        const json = await res.json();
        // dropdown_data returns voyages under 'voyages'
        setVoyagesList(Array.isArray(json.voyages) ? json.voyages : []);
      } catch (e) { /* ignore */ }
    })();

    // Fetch ICDs from the new endpoint
    (async () => {
        try {
            const res = await fetch('/api/masters/icds/', { headers: AUTH_HEADERS() });
            if (res.ok) {
                const data = await res.json();
                setIcdOptions(data.results || data);
            }
        } catch (e) {
            console.error('Failed to fetch ICDs', e);
        }
    })();

  }, []);

  

  const formatAckDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    let hrs = d.getHours();
    const mins = pad(d.getMinutes());
    const secs = pad(d.getSeconds());
    const ampm = hrs >= 12 ? 'PM' : 'AM';
    hrs = hrs % 12;
    if (hrs === 0) hrs = 12;
    const hrsDisp = pad(hrs);
    return `${day}/${month}/${year} ${hrsDisp}:${mins}:${secs} ${ampm}`;
  };

  // Confirm dialog helpers
  const openConfirm = (title, message, onConfirm) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: async () => {
        try {
          if (typeof onConfirm === 'function') {
            await onConfirm();
          }
        } finally {
          setConfirmDialog(d => ({ ...d, open: false }));
        }
      },
      onCancel: () => setConfirmDialog(d => ({ ...d, open: false }))
    });
  };
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  const handleConfirm = async () => {
    if (confirmDialog.onConfirm) await confirmDialog.onConfirm();
    closeConfirm();
  };

  const openManifestDates = (job) => {
    setManifestDatesDialog({
      open: true,
      jobId: job.id,
      idfNo: job.idf_no || '',
      dischargeDate: job.discharge_date || '',
      dischargeTime: (job.discharge_time || '').slice(0, 5),
      carryInEndDate: job.carry_in_end_date || '',
      carryInEndTime: (job.carry_in_end_time || '').slice(0, 5),
      strippingDate: job.stripping_date || '',
      cargoType: job.cargo_type || '',
      saving: false
    });
  };

  const closeManifestDates = () => {
    setManifestDatesDialog(d => ({ ...d, open: false }));
  };

  const openDutyPaidDateDialog = (job) => {
    setDutyPaidDialog({
      open: true,
      jobId: job.id,
      idfNo: job.idf_no || '',
      dutyPaidDate: job.duty_paid_date || new Date().toISOString().split('T')[0],
      saving: false,
    });
  };

  const closeDutyPaidDateDialog = () => {
    setDutyPaidDialog(d => ({ ...d, open: false, saving: false }));
  };

  const saveDutyPaidDate = async () => {
    if (!dutyPaidDialog.jobId) return;
    setDutyPaidDialog(d => ({ ...d, saving: true }));
    const ok = await setDutyPaid(dutyPaidDialog.jobId, true, dutyPaidDialog.dutyPaidDate || new Date().toISOString().split('T')[0]);
    if (ok) {
      closeDutyPaidDateDialog();
    } else {
      pendingHighlightIdRef.current = null;
      setDutyPaidDialog(d => ({ ...d, saving: false }));
    }
  };

  const saveManifestDates = async () => {
    if (!manifestDatesDialog.jobId) return;
    // 3. Add permission check
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
        toast.error("You do not have permission to save changes.");
        return;
    }
    setManifestDatesDialog(d => ({ ...d, saving: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${manifestDatesDialog.jobId}/set-manifest-dates/`, {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify({
          discharge_date: manifestDatesDialog.dischargeDate || null,
          discharge_time: manifestDatesDialog.dischargeTime || null,
          carry_in_end_date: manifestDatesDialog.carryInEndDate || null,
          carry_in_end_time: manifestDatesDialog.carryInEndTime || null,
          stripping_date: manifestDatesDialog.strippingDate || null
        })
      });
      if (!res.ok) {
        const txt = await res.text();
        alert('Failed to save dates: ' + txt);
      } else {
        setRefreshKey(k => k + 1);
        closeManifestDates();
      }
    } catch (e) {
      alert('Failed to save dates');
    } finally {
      setManifestDatesDialog(d => ({ ...d, saving: false }));
    }
  };

  const formatDateDMY = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d)) return val;
    const pad = n => String(n).padStart(2,'0');
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
  };

  const buildDatesTooltip = (job) => {
    const parts = [];
    
    // If LCL, add MBL to tooltip
    const isLcl = (job.shipment_type || '').toString().toUpperCase() === 'LCL' || 
                  (job.cargo_type || '').toString().toUpperCase() === 'LCL';
    if (isLcl) {
        parts.push(`MBL: ${job.mbl_no || job.mbl || job.mblNo || '-'}`);
    }

    // Assuming the backend will provide this field
    if (job.manifest_ready_date) parts.push(`Manifest Ready: ${formatAckDateTime(job.manifest_ready_date)}`);
    if (job.discharge_date) parts.push(`Discharge: ${formatDateDMY(job.discharge_date)}`);
    if (job.carry_in_end_date) parts.push(`Carry In End: ${formatDateDMY(job.carry_in_end_date)}`);
    if (job.stripping_date && (job.cargo_type || '').toUpperCase() === 'LCL') {
        parts.push(`Stripping: ${formatDateDMY(job.stripping_date)}`);
    }    
    return parts.join('\n');
  };


  // open JobForm in readonly mode
  const handleViewJob = async (job) => {
    // reset modal state
    setIsModalOpen(false);
    setCurrentJob(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}${job.id}/`, { headers: AUTH_HEADERS() });
      if (!res.ok) {
        const text = await res.text();
        console.error('Failed to load job details:', text);
        alert('Failed to load job details');
        return;
      }
      const fullJob = await res.json();
      setCurrentJob(fullJob);
      setFormMode('view');
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching job details', err);
      alert('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleFormClose = (refresh = false) => {
      setIsModalOpen(false);
      setCurrentJob(null);
     if (refresh) {
       // trigger manifest list reload
       setRefreshKey(k => k + 1);
     }
  };

  // change voyage - show dropdown then confirm
  const startEditVoyage = (job) => {
    setEditingVoyageJob(job.id);
    setSelectedVoyageId(job.voyage_id || null);
    (async () => {
      try {
        const shippingLineId = job.shipping_line_id || job.shipping_line || job.shippingLineId;
        let url;
        if (shippingLineId) {
          url = `${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/voyages/`;
        } else {
          // fallback to all voyages endpoint
          url = `${MASTERS_API_BASE_URL}voyages/`;
        }
        const res = await fetch(url, { headers: AUTH_HEADERS() });
        if (!res.ok) {
          setEditingVoyageOptions([]);
          return;
        }
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json.results || []);
        setEditingVoyageOptions(list);
      } catch (err) {
        console.error('Failed to load voyages for edit:', err);
        setEditingVoyageOptions([]);
      }
    })();
  };
  const cancelEditVoyage = () => { setEditingVoyageJob(null); setSelectedVoyageId(null); };

  const applyVoyageChange = (row, voyageId) => {
     // build previous and selected labels
    const prevVessel = row.vessel || '';
    const prevVoyNo = row.voyage_number || row.voyage || row.voyage_no || '';
    const prevLabel = [prevVessel, prevVoyNo].filter(Boolean).join(' - ');
    const allVoyageOpts = (editingVoyageJob === row.id ? editingVoyageOptions : voyagesForShippingLine(row.shipping_line_id)) || [];
    const selected = allVoyageOpts.find(v => String(v.id) === String(voyageId));
    const selVessel = selected?.vessel_name || selected?.vessel || '';
    const selMatchingDetail = (selected?.voyage_details || []).find(d => {
        const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
        return slId !== null && String(slId) === String(row.shipping_line_id);
    });
    const selVoyNo = selMatchingDetail?.voyage_number || selected?.voyage_number || selected?.voyage || selected?.voyage_no || '';
    const selLabel = [selVessel, selVoyNo].filter(Boolean).join(' - ');

    openConfirm(
      'Confirm change',
      (<span>
        Change vessel/voyage from <strong>{toUpper(prevLabel || 'N/A')}</strong> to <strong>{toUpper(selLabel || 'N/A')}</strong> for Job <strong>{row.id}</strong>
      </span>),
      async () => {
        // 4. Add permission check
        if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
            toast.error("You do not have permission to perform this action.");
            return;
        }
        try {
          const res = await fetch(`${API_BASE_URL}${row.id}/set-voyage/`, {
            method: 'POST', headers: AUTH_HEADERS(), body: JSON.stringify({ voyage_id: voyageId })
          });
           const payload = await res.json().catch(() => ({}));
           if (!res.ok) {
             const txt = payload?.error || JSON.stringify(payload) || 'Unknown error';
             alert('Failed to set voyage: ' + txt);
             return;
           }
           // If backend returned updated job, update modal JobForm data immediately
           if (payload && payload.job && currentJob && currentJob.id === row.id) {
             setCurrentJob(payload.job);
           }
           // refresh manifest list
           setRefreshKey(k => k + 1);
           cancelEditVoyage();
         } catch (e) {
           alert('Failed to set voyage');
         }
       }
     );
  };

  // change ICD - show select then confirm
  const startEditIcd = (job) => {
    setEditingIcdJob(job.id);
    setSelectedIcd(job.icd || '');
  };
  const cancelEditIcd = () => { setEditingIcdJob(null); setSelectedIcd(''); };

  const applyIcdChange = (row, icdVal) => {
    const prevIcd = row.icd || row.nominated_icd || '';
    openConfirm(
      'Confirm change',
      (<span>
        Change ICD from <strong>{toUpper(prevIcd || 'N/A')}</strong> to <strong>{toUpper(icdVal || 'N/A')}</strong> for Job <strong>{row.id}</strong>
      </span>),
      async () => {
        // 5. Add permission check
        if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
            toast.error("You do not have permission to perform this action.");
            return;
        }
        try {
          const res = await fetch(`${API_BASE_URL}${row.id}/set-icd/`, {
            method: 'POST', headers: AUTH_HEADERS(), body: JSON.stringify({ icd: icdVal })
          });
           if (!res.ok) {
             const txt = await res.text();
             alert('Failed to set ICD: ' + txt);
             return;
           }
           setRefreshKey(k => k + 1);
           cancelEditIcd();
         } catch (e) {
           alert('Failed to set ICD');
         }
       }
     );
  };

  // uncheck duty paid (only shown when duty_paid true)
  const startUnsetDutyPaid = (job) => {
    openConfirm('Confirm', `Mark job ${job.id} as Duty NOT PAID?`, async () => {
      await setDutyPaid(job.id, false);
    });
  };

  // Confirmation for setting Duty Paid status
  const confirmSetDutyPaid = (job, checked) => {
    // 6. Add permission check
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
        toast.error("You do not have permission to change this status.");
        // Re-fetch to revert checkbox state visually
        setRefreshKey(k => k + 1);
        return;
    }
    const nextId = getNextManifestOutRowId(job);
    if (checked) {
      pendingHighlightIdRef.current = nextId;
      openDutyPaidDateDialog(job);
      return;
    }

    openConfirm(
      'Confirm Duty Status',
      `Are you sure you want to mark job ${job.id} as Duty NOT PAID?`,
      async () => {
        const ok = await setDutyPaid(job.id, false, null);
        if (ok) pendingHighlightIdRef.current = nextId;
      }
    );
  };

  const confirmSetCompared = (job, checked) => {
    // 7. Add permission check
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
        toast.error("You do not have permission to change this status.");
        setRefreshKey(k => k + 1);
        return;
    }
    const nextId = getNextManifestOutRowId(job);
   if (checked) {
      // Validate ETB: should be current day or current day + 1
      if (job.etb) {
        const etbDate = new Date(job.etb);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxAllowedDate = new Date(today);
        maxAllowedDate.setDate(today.getDate() + 1);
        maxAllowedDate.setHours(23, 59, 59, 999);
        if (etbDate > maxAllowedDate) {
          toast.error("Document cannot be compared. Check ETB");
          setRefreshKey(k => k + 1);
          return;
        }
      }
      else {
        toast.error("Document cannot be compared. ETB is missing.");
        setRefreshKey(k => k + 1);
        return;
      }

     
      openConfirm(
        'Confirm Comparison',
        (<span>Are you sure you have Compared the file for IDF No <strong>{toUpper(job.idf_no || '-')}</strong>?</span>),
        async () => { const ok = await setCompared(job.id, true); if (ok) pendingHighlightIdRef.current = nextId; }
      );
    } else {
      // Unchecking should also be confirmed and will clear the stripping date
      openConfirm(
        'Confirm Un-Comparison',
        (<span>This will unmark the job as "Compared" and clear the Stripping Date. Are you sure?</span>),
        async () => { const ok = await setCompared(job.id, false); if (ok) pendingHighlightIdRef.current = nextId; }
      );
    }
  };

  // helper to get voyages for a specific shipping line
  const voyagesForShippingLine = (shippingLineId) => {
    if (!shippingLineId) return voyagesList || [];
    return (voyagesList || []).filter(v => {
      // support different serializers / property shapes
      const sid = v.shipping_line_id ?? v.shipping_line ?? (v.shipping_line && (v.shipping_line.id ?? v.shipping_line)) ?? null;
      // loose compare to handle number/string mismatch
      return sid !== null && String(sid) === String(shippingLineId);
    });
  };

  // ICD options: reuse jobform icdOptions list (hard-coded here to match JobForm)
 /* const icdOptions = [
        'AMI',
        'AFICD', 
        'AL-HUSHOOM',
        'AZAM',
        'DICD',
        'ETC CARGO',
        'EAST COST LIQUID',
        'GALCO 025',
        'GALCO 046',
        'JEFAG',
        'KICD',
        'PMM',
        'TRH',
        'HESU',
        'KEFA',
        'SILVER ENTERTRADE',
        'FARION TRADING LTD',
        'TRANS AFRICAN LOGISTICS LIMITED (TALL)',
        'SALISLA',
        'ETC CARGO LIMITED',
        'TANZANIA EAST AFRICA GATEWAY',
        'ROUTE MASTER LOGISTICS COMPANY LIMITED',
        'SSA LOGISTICS',
        'BLOOMER TANZANIA LIMITED',
        'DAR STAR INLAND CONTAINER DEPOT LIMITED',
        'TEAGTL',
        'DP WORLD',
        'ZAMBIA CARGO AND LOGISTICS LIMITED / TZDAR'
    ];*/

  // small render helpers for pen icon (simple inline)
  const PenBtn = ({ onClick, title }) => (
    <button
      type="button"
      className="icon-btn edit-btn"
      onClick={onClick}
      title={title}
      style={{ 
        marginLeft: 6,
        background: 'transparent',
        border: 'none',
        padding: '2px 4px',
        cursor: 'pointer',
        color: '#000',
        fontSize: 12,
        lineHeight: 1
      }}
    >
      <i className="fa fa-pencil" aria-hidden="true"></i>
    </button>
  );

  // Ensure the add-note button is always visible (icon-only)
  const AddNoteBtn = ({ onClick, title = 'Add Note' }) => (
    <button
      type="button"
      className="icon-btn add-btn"
      title={title}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: 4,
        background: '#28a745',
        color: '#fff'
      }}
    >
      <i className="fa fa-plus" aria-hidden="true"></i>
    </button>
  );

  
  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    try {
      const res = await fetch(MANIFEST_REPORT_URL, { headers: AUTH_HEADERS() });
      if (!res.ok) { setJobs([]); if (!isBackgroundRefresh) setLoading(false); return; }
      const data = await res.json();
      const jobList = Array.isArray(data) ? data : [];
      setJobs(jobList);
      const newSet = new Set(jobList.filter(j => j.manifest_out_flash).map(j => j.id));
      setFlashIds(newSet);

        // Standardized TANESW update for both initial load and manual refresh.
      if (!isBackgroundRefresh) {
        toast.info('Auto-updating dates from TANESW in the background...');
      }
      // No need to await this, let it run in the background
      runTaneswBackgroundUpdate(jobList);

    } catch (e) {
      setJobs([]);
      if (!isBackgroundRefresh) {
        toast.error('Failed to fetch report data.');
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresh data when refreshKey changes (after edits)
  useEffect(() => {
    if (refreshKey > 0) {
      fetchData();
    }
  }, [refreshKey, fetchData]);

  // Apply row highlight when data is updated after a Set Duty Paid action
  useEffect(() => {
    if (pendingHighlightIdRef.current) {
      setHighlightJobId(pendingHighlightIdRef.current);
      pendingHighlightIdRef.current = null;
    }
  }, [jobs]);

  // Clear the temporary duty-paid highlight after 5s
  useEffect(() => {
    if (!highlightJobId) return;
    const t = setTimeout(() => setHighlightJobId(null), 5000);
    return () => clearTimeout(t);
  }, [highlightJobId]);


    // helper to build icd nomination display
  function getIcdNominationValue(r) {
    if (!r) return '';
    if (r.nominated_icd || r.nominated_by) {
      const icd = r.nominated_icd || '';
      const by = r.nominated_by || '';
      return (icd && by) ? `${icd} - ${by}` : (icd || by);
    }   
  }

  function getNominationBg(r) {
    if (!r) return undefined;
    const nominatedBy = r.nominated_by || '';
    if (!nominatedBy) return undefined;
    const key = String(nominatedBy).trim().toLowerCase();
    if (key === 'shipper') return '#a6def9ff'; // light blue
    if (key === 'consignee') return '#ccf4bcff'; // light green
    return undefined;
  }


  function renderIcdNominationCell(r) {
    const bg = getNominationBg(r);
    const val = getIcdNominationValue(r);
    return <td style={bg ? { backgroundColor: bg, padding: '6px' } : {}}>{toUpper(val)}</td>;
  }

  function manifestRowProps(job) {
      const acknowledgedUser =
      job.manifest_out_acknowledged_by_username ||
      job.manifest_out_acknowledged_by || // id fallback
      'Unknown';

      const ackDateStr = formatAckDateTime(job.manifest_out_acknowledged_at);
      let titleText = job.manifest_out_acknowledged
        ? `Acknowledged By: ${acknowledgedUser}${ackDateStr ? ` (${ackDateStr})` : ''}`
        : 'Awaiting acknowledgement';

       if (job.compared && !job.wharfage_sent) {
          titleText += '\nWHARFAGE NOT SENT';
      }

      const classes = [
        flashIds.has(job.id) ? 'manifest-flash' : '',
        highlightJobId === job.id ? 'duty-paid-highlight' : ''
      ].filter(Boolean);
      return {
        className: classes.length ? classes.join(' ') : undefined,
        title: titleText,
        style: { cursor: hasPermission('MANIFEST REPORT', 'can_edit') && flashIds.has(job.id) ? 'pointer' : 'default' },
        onDoubleClick: (e) => {
          if (e.target.tagName === 'A' || e.target.closest('button')) return;
          // 9. Check permission before acknowledging
          if (hasPermission('MANIFEST REPORT', 'can_edit')) {
            acknowledgeManifestOut(job);
          }

        }
      };
  }

// sections derived from backend data
const manifestOutAll = jobs.filter(j =>
j.idf_no &&
j.icd &&
j.manifest_ready &&
((String(j.assessment_status || '').toUpperCase().includes('SENT')) || isPreverification(j.assessment_status)) &&
!(j.duty_paid && j.compared)
);

// Sort Manifest Out by ETB ascending, then Vessel + Voyage
const getTime = (v) => {
const t = new Date(v).getTime();
return isNaN(t) ? 0 : t;
};

const compareVesselVoyage = (a, b) => {
const vesselA = toUpper(a.vessel);
const vesselB = toUpper(b.vessel);
if (vesselA !== vesselB) return vesselA.localeCompare(vesselB);
const voyA = toUpper(a.voyage_number);
const voyB = toUpper(b.voyage_number);
return voyA.localeCompare(voyB);
};

const manifestOutSort = (a, b) => {
const etbA = getTime(a.etb);
const etbB = getTime(b.etb);
const hasEtbA = etbA > 0;
const hasEtbB = etbB > 0;

// Records with ETB first, ascending; no-ETB records go to the bottom
if (hasEtbA && !hasEtbB) return -1;
if (!hasEtbA && hasEtbB) return 1;
if (hasEtbA && hasEtbB) {
const etbDiff = etbA - etbB;
if (etbDiff !== 0) return etbDiff;
}

// Tie-break (and no-ETB group): vessel name, then voyage
return compareVesselVoyage(a, b);
};

const sortedManifestOutAll = [...manifestOutAll].sort(manifestOutSort);

// Use sorted list to derive duty-paid splits
// const manifestOutTrue = sortedManifestOutAll.filter(j => !!j.duty_paid);
// const manifestOutFalse = sortedManifestOutAll.filter(j => !j.duty_paid);

// New logic: Split the list based on the 'compared' flag.
const manifestOutCompared = sortedManifestOutAll.filter(j => !!j.compared);
const manifestOutNotCompared = sortedManifestOutAll
.filter(j => !j.compared);


const sentAssessmentSort = (a, b) => {
const hasEtaA = !!a.eta;
const hasEtaB = !!b.eta;

// If both have ETA, sort by ETA ascending then vessel/voyage
if (hasEtaA && hasEtaB) {
const etaDiff = getTime(a.eta) - getTime(b.eta);
if (etaDiff !== 0) return etaDiff;
return compareVesselVoyage(a, b);
}

// If only one has ETA, the one with ETA comes first
if (hasEtaA && !hasEtaB) return -1;
if (!hasEtaA && hasEtaB) return 1;

// If neither has ETA, sort by vessel/voyage
return compareVesselVoyage(a, b);
};

const sentAssessment = jobs.filter(j =>
j.idf_no &&
(!j.manifest_ready || (j.manifest_ready && !j.icd)) &&
((String(j.assessment_status || '').toUpperCase().includes('SENT')) || isPreverification(j.assessment_status))
).sort(sentAssessmentSort);

const pendingAssessment = jobs.filter(j =>
j.idf_no &&
(!j.assessment_status ||
(!(String(j.assessment_status || '').toUpperCase().includes('SENT')) && !isPreverification(j.assessment_status))
)
).sort(sentAssessmentSort);
  const hasVesselVoyage = (r) => !!(r.vessel || r.voyage_number);

  const pendingFiles = jobs.filter(j => !j.idf_no).sort((a, b) => {
    const hasEtaA = !!a.eta;
    const hasEtaB = !!b.eta;

    // Rows with ETA first, sorted by ETA ascending
    if (hasEtaA && !hasEtaB) return -1;
    if (!hasEtaA && hasEtaB) return 1;
    if (hasEtaA && hasEtaB) {
      const etaDiff = getTime(a.eta) - getTime(b.eta);
      if (etaDiff !== 0) return etaDiff;
    }

    const hasA = hasVesselVoyage(a);
    const hasB = hasVesselVoyage(b);
    if (hasA && !hasB) return -1;
    if (!hasA && hasB) return 1;
    return compareVesselVoyage(a, b);
  });

  const acknowledgeManifestOut = async (job) => {
    if (!flashIds.has(job.id)) return;
    // 10. Add permission check
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) return;

    // optimistic update
    const newSet = new Set(flashIds);
    newSet.delete(job.id);
    setFlashIds(newSet);
    try {
      await fetch(`${API_BASE_URL}${job.id}/acknowledge-manifest-out/`, {
        method: 'POST',
        headers: AUTH_HEADERS()
      });
    } catch {
      // on failure re-add (optional)
      const newSet = new Set(flashIds);
      newSet.add(job.id);
      setFlashIds(newSet);
    }
  };



  const matchesSearch = row => {
    if (idfNo && !String(row.idf_no || '').toLowerCase().includes(idfNo.toLowerCase())) {
      return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return [
      String(row.id || '').toLowerCase(),
      String(row.idf_no || '').toLowerCase(),
      String(row.client_ref || '').toLowerCase(),
      String(row.vessel || '').toLowerCase(),
      String(row.customer_name || '').toLowerCase(),
      String(row.bl_value || '').toLowerCase()
    ].some(v => v && v.includes(s));
  };

  const applyFilter = list => list.filter(matchesSearch);

  const getNextManifestOutRowId = (job) => {
    const compared = applyFilter(manifestOutCompared);
    const idx = compared.findIndex(j => j.id === job.id);
    if (idx >= 0 && compared[idx + 1]) return compared[idx + 1].id;
    const notComp = applyFilter(manifestOutNotCompared);
    const nidx = notComp.findIndex(j => j.id === job.id);
    return notComp[nidx + 1]?.id || null;
  };

  // duty paid save triggered by checkbox
  async function setDutyPaid(jobId, value, dutyPaidDate = null) {
    setSavingDutyFor(jobId);
    try {
      const payload = { duty_paid: !!value };
      if (value) {
        payload.duty_paid_date = dutyPaidDate || new Date().toISOString().split('T')[0];
      } else {
        payload.duty_paid_date = null;
      }

      const res = await fetch(`${API_BASE_URL}${jobId}/set-duty-paid/`, {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setRefreshKey(k => k + 1);
        return true;
      } else {
        const err = await res.text();
        alert('Failed to update Duty Paid: ' + err);
        return false;
      }
    } catch (e) {
      alert('Failed to update Duty Paid');
      return false;
    } finally {
      setSavingDutyFor(null);
    }
  }

  // Compared setter
  async function setCompared(jobId, value) {
    setSavingComparedFor(jobId);
    try {
      const payload = { compared: !!value };
      // If we are marking as compared, set the stripping date to today
      if (value) {
        payload.stripping_date = new Date().toISOString().split('T')[0];
      } else {
        // If un-comparing, clear the stripping date
        payload.stripping_date = null;
      }

      const res = await fetch(`${API_BASE_URL}${jobId}/set-compared/`, {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setRefreshKey(k => k + 1);
        return true;
      } else {
        const err = await res.text();
        alert('Failed to update Compared: ' + err);
        return false;
      }
    } catch (e) {
      alert('Failed to update Compared');
      return false;
    } finally {
      setSavingComparedFor(null);
    }
  }

  // --- Tanesw Tracking ---
  const fetchAndProcessTaneswData = async (job, options = {}) => {
    const { isBackgroundTask = false } = options;
    const blNumber = getBlValue(job);
    if (!blNumber || blNumber === '-') {
      if (!isBackgroundTask) {
        toast.warn('Cannot track: BL Number is missing for this job.');
      }
      return null;
    }

    try {
      const url = `https://tanesw.tra.go.tz/api/cgm/api/v1/cgme/trkng/srch?srchCrn=&srchBlNo=${encodeURIComponent(blNumber)}&srchCntrNo=`;
      const response = await fetch(url);

      if (!response.ok) {
        // Silently fail for background tasks, show error for manual clicks
        if (!isBackgroundTask) toast.error(`Tanesw API request failed: ${response.status}`);
        return null;
      }

      const apiData = await response.json();
      // Primary check should be for the existence of the totalCnt structure.
      if (typeof apiData?.totalCnt?.content === 'undefined') {
        if (!isBackgroundTask) toast.error('Invalid data structure from Tanesw API.');
        return null;
      }

      const totalContent = apiData.totalCnt.content;
      const updates = {};
      let manifestReadyUpdate = null;
      let isManifestOut = false;

      // Tanesw can return multiple trkngDtl nodes (one per discharge port / transit country).
      // Manifest is only considered "out" for a node whose discharge port is TZDAR (Dar es Salaam).
      const trkngDtlList = Array.isArray(apiData.trkngDtl?.content)
        ? apiData.trkngDtl.content
        : (apiData.trkngDtl?.content ? [apiData.trkngDtl.content] : []);
      const trkngDtlContent = trkngDtlList.find(n => n?.dscgPortCd === 'TZDAR');

      console.log('Total Content:', totalContent);
      if (trkngDtlContent) {
        isManifestOut = true;
        console.log('Manifest Out:', isManifestOut);
      }

      // Also, only check for manifest ready if content exists
      if (isManifestOut && !job.manifest_ready) {
        manifestReadyUpdate = {
            voyage_id: job.voyage_id,
            tra_manifest_ready: formatDateTimeForBackend(new Date())
        };
        console.log('Manifest Ready Update:', manifestReadyUpdate);
      }

      // Only proceed if a TZDAR discharge node was found to process.
      if (trkngDtlContent) {
        const processList = trkngDtlContent.blPrcssLst;

        // Update ICD if not present
        const taneswIcdCode = trkngDtlContent.dlvrPlcCd;
        if (taneswIcdCode && !job.icd) {
            updates.icd = taneswIcdCode.toUpperCase();
        }

        if (!processList && !taneswIcdCode && !manifestReadyUpdate) {
            if (!isBackgroundTask) toast.warning('Invalid data structure: Process list missing despite content count > 0.');
            return null;
        }

        if (processList) {
          const dischargeEntry = processList.find(p => p.cagTrkngPrcssCd === 'B10');
          const carryInEndEntry = processList.find(p => p.cagTrkngPrcssCd === 'C04');

          const splitTaneswDateTime = (val) => {
            const s = String(val || '').trim();
            if (!s) return { date: '', time: '' };
            const parts = s.split(' ');
            const timePart = (parts[1] || '').slice(0, 5);
            return { date: parts[0] || '', time: /^\d{2}:\d{2}$/.test(timePart) ? timePart : '' };
          };

          if (dischargeEntry) {
            const { date, time } = splitTaneswDateTime(dischargeEntry.prcssDt);
            if (!job.discharge_date && date) updates.discharge_date = date;
            if (!job.discharge_time && time) updates.discharge_time = time;
          }
          if (carryInEndEntry) {
            const { date, time } = splitTaneswDateTime(carryInEndEntry.prcssDt);
            if (!job.carry_in_end_date && date) updates.carry_in_end_date = date;
            if (!job.carry_in_end_time && time) updates.carry_in_end_time = time;
          }

        }
        
      }

      let jobDatesUpdated = false;
      let voyageDateUpdated = false;
      if (manifestReadyUpdate && manifestReadyUpdate.voyage_id) {
          try {
            const res = await fetch(`${MASTERS_API_BASE_URL}vessel-voyages/${manifestReadyUpdate.voyage_id}/set-manifest-ready/`, {
                method: 'POST',
                headers: AUTH_HEADERS(),
                body: JSON.stringify({ tra_manifest_ready: manifestReadyUpdate.tra_manifest_ready })
            });
            if (res.ok) {
                voyageDateUpdated = true;
            } else {
                // Log error but don't block UI for background tasks
                const errText = await res.text();
                console.error(`Failed to set manifest ready for voyage ${manifestReadyUpdate.voyage_id}:`, errText);
                if (!isBackgroundTask) {
                    toast.error('Failed to update Voyage Manifest Ready date.');
                }
            }
          } catch (e) {
             console.error(`Error calling set-manifest-ready for voyage ${manifestReadyUpdate.voyage_id}:`, e);
          }
      }

      if (Object.keys(updates).length > 0) {
        await fetch(`${API_BASE_URL}${job.id}/set-manifest-dates/`, {
          method: 'POST',
          headers: AUTH_HEADERS(),
          body: JSON.stringify(updates)
        });
        jobDatesUpdated = true;
      }
      return (jobDatesUpdated || voyageDateUpdated) ? { ...updates, voyage_updated: voyageDateUpdated } : null;

    } catch (err) {
      // Log error for backend, show toast for frontend user action
      console.error(`Tanesw tracking error for BL ${blNumber}:`, err);
      if (!isBackgroundTask) {
        toast.error('An error occurred while fetching Tanesw data.');
      }
      return null;
    }
  };

  const handleBlClick = async (job) => {
    // 11. Check permission before allowing any edit-related action
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
        // For view-only users, just show the tooltip, don't open a dialog.
        // The link styling is handled later in the JSX.
        return;
    }

    // If both dates already exist, just open the dialog without fetching.
    if (job.discharge_date && job.carry_in_end_date) {
      openManifestDates(job);
      return;
    }

    // Otherwise, proceed with fetching data from Tanesw.
    toast.info(`Fetching TANESW data for BL: ${getBlValue(job)}...`);
    const taneswResult = await fetchAndProcessTaneswData(job);
    const updates = taneswResult ? taneswResult : null;

    if (updates) {
      toast.success('Dates successfully pulled from TANESW.');
      // Open the dialog with the potentially updated dates
      setManifestDatesDialog({
        open: true,
        jobId: job.id,
        idfNo: job.idf_no || '',
        dischargeDate: updates.discharge_date || job.discharge_date || '',
        dischargeTime: (updates.discharge_time || job.discharge_time || '').slice(0, 5),
        carryInEndDate: updates.carry_in_end_date || job.carry_in_end_date || '',
        carryInEndTime: (updates.carry_in_end_time || job.carry_in_end_time || '').slice(0, 5),
        strippingDate: job.stripping_date || '',
        cargoType: job.cargo_type || '',
        saving: false
      });
      setRefreshKey(k => k + 1); // Refresh list to show new data
    } else {
      toast.info('No new dates to update from TANESW.');
      // Still open the dialog to show existing dates
      openManifestDates(job);
    }
  };

const runTaneswBackgroundUpdate = async (jobList) => {
    // Filter for all jobs that have a valid BL number to check.
    const allJobsToUpdate = jobList.filter(job => getBlValue(job) && getBlValue(job) !== '-');
    
    let successCount = 0;
    const newPendingManifestOutIds = new Set();

    // Identify jobs in pending sections to check for manifest status
    const sentAssessmentJobs = jobList.filter(j =>
      j.idf_no && (!j.manifest_ready || (j.manifest_ready && !j.icd)) && (String(j.assessment_status || '').toUpperCase().includes('SENT'))
    );
    const pendingAssessmentJobs = jobList.filter(j =>
        j.idf_no && (!j.assessment_status || !(String(j.assessment_status || '').toUpperCase().includes('SENT')))
    );

    for (const job of allJobsToUpdate) {
      // fetchAndProcessTaneswData returns { updates, isManifestOut }
      const result = await fetchAndProcessTaneswData(job, { isBackgroundTask: true });
      if (result && result.updates) {
        successCount++;
      }
      
      // Check if it's a pending job and manifest is out
      if (result && result.isManifestOut) {
        const isPending = sentAssessmentJobs.some(p => p.id === job.id) || pendingAssessmentJobs.some(p => p.id === job.id);
        if (isPending) {
            newPendingManifestOutIds.add(job.id);
        }
      }
    }
    setPendingManifestOutIds(newPendingManifestOutIds);

    if (successCount > 0) {
      toast.success(`Successfully updated data for ${successCount} job(s). Refreshing list.`);
      setRefreshKey(k => k + 1);
    } else {
      toast.info('Background check complete. No new data found from TANESW.');
    }
  };

  
  // ETB/ATB dialog handlers
  const openEtbAtbDialog = (job) => {
    setEtbAtbDialog({
      open: true,
      voyageId: job.voyage_id,
      etb: job.etb || '',
      atb: job.atb || '',
      ata: job.ata || '',
      dischargeDate: job.discharge_date || '',
      saving: false,
      error: ''
    });
  };

  const closeEtbAtbDialog = () => setEtbAtbDialog(d => ({ ...d, open: false }));

  const saveEtbAtb = async () => {
    if (!etbAtbDialog.voyageId) return;
    // Validate ETB/ATB cannot be before ATA
    if (etbAtbDialog.ata) {
      const fmt = d => new Date(d).toLocaleDateString('en-GB');
      if (etbAtbDialog.etb && etbAtbDialog.etb < etbAtbDialog.ata) {
        setEtbAtbDialog(d => ({
          ...d,
          error: 'ETB cannot be before ATA'
        }));
        return;
      }
      if (etbAtbDialog.atb && etbAtbDialog.atb < etbAtbDialog.ata) {
        setEtbAtbDialog(d => ({
          ...d,
          error: 'ATB cannot be before ATA'
        }));
        return;
      }
    }
    // Validate ATB does not exceed Discharge Date
    if (etbAtbDialog.atb && etbAtbDialog.dischargeDate && etbAtbDialog.atb > etbAtbDialog.dischargeDate) {
      const fmt = d => new Date(d).toLocaleDateString('en-GB');
      setEtbAtbDialog(d => ({
        ...d,
        error: `ATB (${fmt(etbAtbDialog.atb)}) cannot be after Discharge Date (${fmt(etbAtbDialog.dischargeDate)}).`
      }));
      return;
    }
    setEtbAtbDialog(d => ({ ...d, saving: true, error: '' }));
    try {
      const res = await fetch(`${MASTERS_API_BASE_URL}vesselvoyages/${etbAtbDialog.voyageId}/set-etb-atb/`, {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify({
          etb: etbAtbDialog.etb || null,
          atb: etbAtbDialog.atb || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail ? (Array.isArray(err.detail) ? err.detail.join(' ') : err.detail)
          : err.error || (typeof err === 'object' && Object.values(err)[0]) || 'Failed to save ETB/ATB';
        setEtbAtbDialog(d => ({ ...d, error: String(msg) }));
        toast.error(String(msg));
      } else {
        setRefreshKey(k => k + 1);
        closeEtbAtbDialog();
      }
    } catch (e) {
      alert('Failed to save ETB/ATB');
    } finally {
      setEtbAtbDialog(d => ({ ...d, saving: false }));
    }
  };

  // show-on-manifest notes posting
  const openAddNoteDialog = (jobId) => {
    setNoteJobId(jobId);
    setNoteSubject('');
    setNoteDetails('');
    setNoteDialogOpen(true);
  };

  const closeNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteJobId(null);
    setNoteSubject('');
    setNoteDetails('');
  };

  const saveNote = async () => {
    if (!noteJobId) return;
    if (!noteSubject.trim() && !noteDetails.trim()) {
      alert('Enter subject or details for note.');
      return;
    }
    // 12. Add permission check
    if (!hasPermission('MANIFEST REPORT', 'can_edit')) {
        toast.error("You do not have permission to add notes.");
        return;
    }
    setSavingNote(true);
    try {
      const payload = {
        notes: [{
          subject: noteSubject.trim().toUpperCase(),
          notes: noteDetails.trim().toUpperCase(),
          note_date: new Date().toISOString().split('T')[0], // current date YYYY-MM-DD
          show_on_manifest: true
        }]
      };
      const res = await fetch(`${API_BASE_URL}${noteJobId}/notes/`, {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setRefreshKey(k => k + 1);
        closeNoteDialog();
      } else {
        const txt = await res.text();
        alert('Failed to save note: ' + txt);
      }
    } catch (err) {
      alert('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const hasManifestNotes = (job) => (job.notes || []).some(n => n.show_on_manifest === true);

  function renderNotes(job) {
    const notes = (job.notes || []).filter(n => n.show_on_manifest === true);
    if (!notes.length) {
      return (
        <div>
          <span>-</span>
          {/* 13. Conditionally render the Add Note button */}
          {hasPermission('MANIFEST REPORT', 'can_edit') && (
            <div style={{ marginTop: 6 }}>
              <AddNoteBtn onClick={() => openAddNoteDialog(job.id)} />
            </div>
          )}
        </div>
      );
    }

    // sort by note_date (or created_at) descending
    const sorted = [...notes].sort((a, b) => {
      const da = new Date(a.note_date || 0).getTime();
      const db = new Date(b.note_date || 0).getTime();
      return db - da;
    });

    return (
      <div>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {sorted.map(n => {
            const tooltip = (n.details || n.notes || '').toString();
            return (
              <li key={n.id || `${n.subject}-${n.note_date || n.created_at || Math.random()}`}>
                <span title={tooltip}>{n.subject || '(no subject)'}</span>
              </li>
            );
          })}
        </ul>
        {/* 14. Conditionally render the Add Note button */}
        {hasPermission('MANIFEST REPORT', 'can_edit') && (
            <AddNoteBtn onClick={() => openAddNoteDialog(job.id)} />
        )}
      </div>
    );
  }

  function renderContainerCell(job) {
    const cs = job.container_summary;
    if (!cs) return '-';
    if (cs === 'LCL') return 'LCL';
    if (Array.isArray(cs)) {
      return (<ul style={{ margin: 0, paddingLeft: 16 }}>{cs.map((s, i) => <li key={i}>{s}</li>)}</ul>);
    }
    if (typeof cs === 'string') {
      const parts = cs.split('\n').map(s => s.trim()).filter(Boolean);
      if (parts.length === 0) return '-';
      if (parts.length === 1) return parts[0];
      return (<ul style={{ margin: 0, paddingLeft: 16 }}>{parts.map((s, i) => <li key={i}>{s}</li>)}</ul>);
    }
    return '-';
  }

  function renderDangerousClass(job) {
    const dc = job.dangerous_class;
    if (!dc) return '-';
    const arr = Array.isArray(dc) ? dc : (typeof dc === 'string' ? dc.split(',').map(s => s.trim()).filter(Boolean) : []);
    if (arr.length === 0) return '-';
    // display only class part before ' - ' and as ul
    return (<ul style={{ margin: 0, paddingLeft: 16 }}>
      {arr.map((item, i) => {
        const first = item.split(' - ')[0].trim();
        return <li key={i}>{toUpper(first)}</li>;
      })}
    </ul>);
  }

  function getBlValue(job) {
    const isLcl = (job.shipment_type || '').toString().toUpperCase() === 'LCL' || 
                  (job.cargo_type || '').toString().toUpperCase() === 'LCL';
    if (isLcl) {
      return job.hbl_so_no || job.hblSoNo || job.bl_no || '-';
    }
    return job.mbl_no || job.mbl || job.mblNo || '-';
  }

  // Exports same as before - using manifest url data fields
  function buildExportRows(list, isManifestOut=false) {
    return list.map(r => ({
      ID: r.id,
      'IDF NO': r.idf_no || '-',
      'DISCHARGE DATE': r.discharge_date || '',
      'CARRY IN END DATE': r.carry_in_end_date || '',
      'STRIPPING DATE': r.stripping_date || '',
      VESSEL: r.vessel || '',
      DATE: (isManifestOut ? (r.etb || '') : (r.eta || '')),
      'CUSTOMER NAME': r.customer_name || '',
      BL: getBlValue(r),
      'CONTAINER SIZE': (r.container_summary === 'LCL' ? 'LCL' : Array.isArray(r.container_summary) ? r.container_summary.join('; ') : r.container_summary),
      ICD: r.icd || '',
      'DANGEROUS CLASS': Array.isArray(r.dangerous_class) ? r.dangerous_class.join(', ') : r.dangerous_class || '',
      'ICD NOMINATION': getIcdNominationValue(r) || '',
      'DUTY PAID': r.duty_paid ? 'YES' : 'NO',
      NOTES: (r.notes || []).filter(n => n.show_on_manifest).map(n => n.subject).join('; '),
      'CLIENT REF': r.client_ref || ''
    }));
  }

  function exportExcelSection(list, filename, isManifestOut=false) {
    const rows = buildExportRows(list, isManifestOut);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, filename);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }

  function exportPDFSection(list, title, isManifestOut=false) {
    const doc = new jsPDF();
    doc.text(title, 10, 10);
    const rows = list.map(r => [
      r.id,
      r.idf_no,
      r.vessel,
      (isManifestOut ? (fmtDateShort(r.etb) || '') : (fmtDateShort(r.eta) || '')),
      r.customer_name,
      getBlValue(r),
      Array.isArray(r.container_summary) ? r.container_summary.join('; ') : r.container_summary,
      r.icd,
      Array.isArray(r.dangerous_class) ? r.dangerous_class.join(', ') : r.dangerous_class,
      getIcdNominationValue(r),
      (r.notes || []).filter(n => n.show_on_manifest).map(n => n.subject).join('; ')
    ]);
    if (isManifestOut) {
      doc.autoTable({ startY: 16, head: [['ID','IDF NO','VESSEL','ETB','CUSTOMER','BL','CONTAINER SIZE','ICD','DANG CLASS','ICD NOM','NOTES']], body: rows, styles: { fontSize: 8 } });
    } else {
      doc.autoTable({ startY: 16, head: [['ID','IDF NO','VESSEL','ETA','CUSTOMER','BL','CONTAINER SIZE','ICD','DANG CLASS','ICD NOM','NOTES']], body: rows, styles: { fontSize: 8 } });
    }
    doc.save(`${title}.pdf`);
  }

  // 15. Add top-level permission check for viewing the report
  if (!hasPermission('MANIFEST REPORT', 'can_view')) {
    return (
        <div className="job-list-container" style={{ overflowX: 'auto' }}>
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this report.</p>
        </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Manifest Report</h2>
        <button onClick={() => fetchData()} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>

      <div className="toolbar" style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <Select2Dropdown options={[
            { id: 'ALL', text: 'ALL' },
            { id: 'MANIFEST_OUT', text: 'MANIFEST OUT' },
            { id: 'SENT_ASSESSMENT', text: 'SENT ASSESSMENT (WAITING FOR MANIFEST)' },
            { id: 'PENDING_ASSESSMENT', text: 'PENDING ASSESSMENT' },
            { id: 'PENDING_FILES', text: 'PENDING FILES' }
          ]} value={sectionFilter} onChange={setSectionFilter} displayKey="text" valueKey="id" placeholder="Section" style={{ minWidth: 220 }} />
        <input type="text" placeholder="IDF NO" value={idfNo} onChange={e => setIdfNo(e.target.value)} className="search-input" />
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
        <button onClick={() => fetchData(false)} className="add-btn" disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
        <button onClick={() => exportExcelSection(jobs, 'ManifestReport_All')} className="add-btn" style={{ backgroundColor: '#007bff' }}>Download Excel</button>
        <button onClick={() => exportPDFSection(jobs, 'ManifestReport_All')} className="add-btn" style={{ backgroundColor: '#17a2b8' }}>Download PDF</button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
          Loading manifest report...
        </div>
      )}

      {(sectionFilter === 'ALL' || sectionFilter === 'MANIFEST_OUT') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, background: '#e3f2fd', borderRadius: 6, padding: 8, fontSize: 16 }}>MANIFEST OUT</div>

          {/* Top: Compared true */}
          <div>
          <table className="jobs-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>IDF NO</th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span>
                  <br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'normal' }}>ETB</span>
                  <br /><span style={{ fontWeight: 'bold' }}>ATB</span>
                </th>
                <th>CUSTOMER NAME</th><th>CLIENT REF</th><th>BL</th><th>CONTAINER SIZE</th><th>ICD</th><th>DANGEROUS CLASS</th><th>DUTY PAID</th><th>COMPARED</th><th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {applyFilter(manifestOutCompared).length === 0 ? (
                <tr><td colSpan={12} className="no-data">No records found.</td></tr>
              ) : applyFilter(manifestOutCompared).map(r => (
                <tr key={`mout-true-${r.id}`} {...manifestRowProps(r)}>
                  <td>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(r); }}>
                      {r.id}
                    </a>
                  </td>
                  <td>
                    {r.idf_no ? (
                      <span>{r.idf_no}</span>
                    ) : '-'}
                    {hasManifestNotes(r) && (
                      <div className="check-notes-flash">CHECK NOTES</div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'bold' }}>{toUpper(r.vessel)}</span>
                    <br />
                    <span style={{ fontWeight: 'normal' }}>
                      {toUpper(r.voyage_number)}
                    </span>
                    {editingVoyageJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditVoyage(r)} title="Edit vessel / voyage" />
                    )}

                    {editingVoyageJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={(
                            (editingVoyageJob === r.id ? editingVoyageOptions : voyagesForShippingLine(r.shipping_line_id)) || []
                          ).map(v => {
                            const vesselName = v.vessel_name || v.vessel || v.vesselName || '';
                            const matchingDetail = (v.voyage_details || []).find(d => {
                              const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
                              return slId !== null && String(slId) === String(r.shipping_line_id);
                            });
                            const voyageNo = matchingDetail?.voyage_number || v.voyage_number || v.voyage || v.voyage_no || v.voyageNumber || '';
                            const label = voyageNo ? `${vesselName} ${voyageNo}`.trim() : vesselName || (v.name || '');
                            return { id: v.id, text: label || String(v.id) };
                          })}
                          value={selectedVoyageId}
                          onChange={(val) => setSelectedVoyageId(val)}
                          valueKey="id"
                          displayKey="text"
                          noOptionsMessage="NO VESSEL VOYAGE FOR THE SELECTED SHIPPING LINE AVAILABLE"
                          placeholder="Select voyage"
                          style={{ minWidth: 220 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyVoyageChange(r, selectedVoyageId)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditVoyage}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'normal' }}>{fmtDateShort(r.etb)}</span>
                    <br />
                    <span style={{ fontWeight: 'bold' }}>
                      {fmtDateShort(
                        r.atb ||
                        (r.berthing_date) ||
                        (r.vessel_details && (r.vessel_details.berthing_date || r.vessel_details.atb)) ||
                        ''
                      )}
                    </span>
                    {hasPermission('MANIFEST REPORT', 'can_edit') && rawHasPermission('VESSEL VOYAGES', 'can_edit') && r.voyage_id && (
                      <PenBtn onClick={() => openEtbAtbDialog(r)} title="Edit ETB / ATB" />
                    )}
                  </td>
                  <td>{toUpper(r.customer_name)}</td>
                  <td>{r.client_ref || '-'}</td>
                  <td>
                    {/* 17. Conditionally render BL as a link or plain text */
                    hasPermission('MANIFEST REPORT', 'can_edit') ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); handleBlClick(r); }} title={buildDatesTooltip(r)}>
                            {toUpper(getBlValue(r))}
                        </a>
                    ) : (
                        <span title={buildDatesTooltip(r)}>{toUpper(getBlValue(r))}</span>
                    )}
                  </td>
                  <td>{renderContainerCell(r)}</td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{toUpper(r.icd || '')}</span>
                    {/* 18. Conditionally render the edit ICD button */}
                    {editingIcdJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditIcd(r)} title="Edit ICD" />
                    )}
                    {editingIcdJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={icdOptions.map(i => ({ id: i.name, text: i.name }))}
                          value={selectedIcd}
                          onChange={(val) => setSelectedIcd(val)}
                          valueKey="id"
                          displayKey="text"
                          placeholder="Select ICD"
                          style={{ minWidth: 180 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyIcdChange(r, selectedIcd)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditIcd}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{renderDangerousClass(r)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {isPreverification(r.assessment_status) ? (
                      <span style={{ color: 'red', fontWeight: 'bold' }}>PRE-V</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!r.duty_paid}
                        disabled={savingDutyFor === r.id || !hasPermission('MANIFEST REPORT', 'can_edit')} // 19. Disable checkbox
                        onChange={(e) => confirmSetDutyPaid(r, e.target.checked)}
                      />
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isPreverification(r.assessment_status) ? (
                      <span>-</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => confirmSetCompared(r, false)}
                        title="Click to mark as Not Compared"
                        style={{ cursor: hasPermission('MANIFEST REPORT', 'can_edit') ? 'pointer' : 'default' }}
                        disabled={!hasPermission('MANIFEST REPORT', 'can_edit')} // 20. Disable checkbox
                      />
                    )}
                  </td>

                  <td>{renderNotes(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div style={{ height: 12 }} />

          {/* Bottom: Duty Paid false (checkbox triggers save) */}
          <div>
          <table className="jobs-table" style={{ marginTop: 8, overflow: 'visible' }}>
            <thead>
              <tr>
                <th>ID</th><th>IDF NO</th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span>
                  <br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'normal' }}>ETB</span>
                  <br /><span style={{ fontWeight: 'bold' }}>ATB</span>
                </th>
                <th>CUSTOMER NAME</th><th>CLIENT REF</th><th>BL</th><th>CONTAINER SIZE</th><th>ICD</th><th>DANGEROUS CLASS</th><th>DUTY PAID</th><th>COMPARED</th><th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {applyFilter(manifestOutNotCompared).length === 0 ? (
                <tr><td colSpan={12} className="no-data">No records found.</td></tr>
              ) : applyFilter(manifestOutNotCompared).map(r => (
                <tr key={`mout-false-${r.id}`} {...manifestRowProps(r)}>
                  <td>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(r); }}>
                      {r.id}
                    </a>
                  </td>
                   <td>
                    {r.idf_no ? (
                        <span>{r.idf_no}</span>
                    ) : '-'}
                    {hasManifestNotes(r) && (
                      <div className="check-notes-flash">CHECK NOTES</div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'bold' }}>{toUpper(r.vessel)}</span>
                    <br />
                    <span style={{ fontWeight: 'normal' }}>
                      {toUpper(r.voyage_number)}
                    </span>
                    {editingVoyageJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditVoyage(r)} title="Edit vessel / voyage" />
                    )}
                    {editingVoyageJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={(
                            (editingVoyageJob === r.id ? editingVoyageOptions : voyagesForShippingLine(r.shipping_line_id)) || []
                          ).map(v => {
                            const vesselName = v.vessel_name || v.vessel || v.vesselName || '';
                            const matchingDetail = (v.voyage_details || []).find(d => {
                              const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
                              return slId !== null && String(slId) === String(r.shipping_line_id);
                            });
                            const voyageNo = matchingDetail?.voyage_number || v.voyage_number || v.voyage || v.voyage_no || v.voyageNumber || '';
                            const label = voyageNo ? `${vesselName} ${voyageNo}`.trim() : vesselName || (v.name || '');
                            return { id: v.id, text: label || String(v.id) };
                          })}
                          value={selectedVoyageId}
                          onChange={(val) => setSelectedVoyageId(val)}
                          valueKey="id"
                          displayKey="text"
                          noOptionsMessage="NO VESSEL VOYAGE FOR THE SELECTED SHIPPING LINE AVAILABLE"
                          placeholder="Select voyage"
                          style={{ minWidth: 220 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyVoyageChange(r, selectedVoyageId)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditVoyage}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'normal' }}>{fmtDateShort(r.etb)}</span>
                    <br />
                    <span style={{ fontWeight: 'bold' }}>
                      {fmtDateShort(
                        r.atb ||
                        (r.berthing_date) ||
                        (r.vessel_details && (r.vessel_details.berthing_date || r.vessel_details.atb)) ||
                        ''
                      )}
                    </span>
                    {hasPermission('MANIFEST REPORT', 'can_edit') && rawHasPermission('VESSEL VOYAGES', 'can_edit') && r.voyage_id && (
                      <PenBtn onClick={() => openEtbAtbDialog(r)} title="Edit ETB / ATB" />
                    )}
                  </td>
                  <td>{toUpper(r.customer_name)}</td>
                  <td>{r.client_ref || '-'}</td>
                  <td>
                    {/* 22. Conditionally render BL as a link or plain text */
                    hasPermission('MANIFEST REPORT', 'can_edit') ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); handleBlClick(r); }} title={buildDatesTooltip(r)}>
                            {toUpper(getBlValue(r))}
                        </a>
                    ) : (
                        <span title={buildDatesTooltip(r)}>{toUpper(getBlValue(r))}</span>
                    )}
                  </td>
                  <td>{renderContainerCell(r)}</td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{toUpper(r.icd || '')}</span>
                    {/* 23. Conditionally render the edit ICD button */}
                    {editingIcdJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditIcd(r)} title="Edit ICD" />
                    )}
                    {editingIcdJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={icdOptions.map(i => ({ id: i.name, text: i.name }))}
                          value={selectedIcd}
                          onChange={(val) => setSelectedIcd(val)}
                          valueKey="id"
                          displayKey="text"
                          placeholder="Select ICD"
                          style={{ minWidth: 180 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyIcdChange(r, selectedIcd)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditIcd}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{renderDangerousClass(r)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {isPreverification(r.assessment_status) ? (
                      <span style={{ color: 'red', fontWeight: 'bold' }}>PRE-V</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!r.duty_paid}
                        disabled={savingDutyFor === r.id || !hasPermission('MANIFEST REPORT', 'can_edit')} // 24. Disable checkbox
                        onChange={(e) => confirmSetDutyPaid(r, e.target.checked)}
                      />
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isPreverification(r.assessment_status) ? (
                      <span>-</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!r.compared}
                        disabled={savingComparedFor === r.id || !hasPermission('MANIFEST REPORT', 'can_edit')} // 25. Disable checkbox
                        onChange={(e) => confirmSetCompared(r, e.target.checked)}
                      />
                    )}
                  </td>
                  <td>{renderNotes(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {(sectionFilter === 'ALL' || sectionFilter === 'SENT_ASSESSMENT') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, background: '#f3dcb8ff', borderRadius: 6, padding: 8, fontSize: 16 }}>SENT ASSESSMENT (WAITING FOR MANIFEST)</div>
          <div>
          <table className="jobs-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th>ID</th><th>IDF NO</th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span>
                  <br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th>ETA</th><th>CUSTOMER NAME</th><th>CLIENT REF</th><th>BL</th><th>CONTAINER SIZE</th><th>ICD</th><th>DANGEROUS CLASS</th><th>ICD NOMINATION</th><th>DUTY PAID</th><th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {applyFilter(sentAssessment).length === 0 ? (
                <tr><td colSpan={12} className="no-data">No records found.</td></tr>
              ) : applyFilter(sentAssessment).map(r => (
                <tr key={`sent-${r.id}`}
                    className={pendingManifestOutIds.has(r.id) ? 'manifest-out-pending-flash' : undefined}
                    title={pendingManifestOutIds.has(r.id) ? 'MANIFEST IS OUT' : undefined}
                >
                  <td>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(r); }}>
                      {r.id}
                    </a>
                  </td>
                  <td>{r.idf_no || '-'}</td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'bold' }}>{toUpper(r.vessel)}</span>
                    <br />
                    <span style={{ fontWeight: 'normal' }}>
                      {toUpper(r.voyage_number)}
                    </span>
                    {editingVoyageJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditVoyage(r)} title="Edit vessel / voyage" />
                    )}
                    {editingVoyageJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={(
                            (editingVoyageJob === r.id ? editingVoyageOptions : voyagesForShippingLine(r.shipping_line_id)) || []
                          ).map(v => {
                            const vesselName = v.vessel_name || v.vessel || v.vesselName || '';
                            const matchingDetail = (v.voyage_details || []).find(d => {
                              const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
                              return slId !== null && String(slId) === String(r.shipping_line_id);
                            });
                            const voyageNo = matchingDetail?.voyage_number || v.voyage_number || v.voyage || v.voyage_no || v.voyageNumber || '';
                            const label = voyageNo ? `${vesselName} ${voyageNo}`.trim() : vesselName || (v.name || '');
                            return { id: v.id, text: label || String(v.id) };
                          })}
                          value={selectedVoyageId}
                          onChange={(val) => setSelectedVoyageId(val)}
                          valueKey="id"
                          displayKey="text"
                          noOptionsMessage="NO VESSEL VOYAGE FOR THE SELECTED SHIPPING LINE AVAILABLE"
                          placeholder="Select voyage"
                          style={{ minWidth: 220 }}
                          clearable={true}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyVoyageChange(r, selectedVoyageId)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditVoyage}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{fmtDateShort(r.eta)}</td>
                  <td>{toUpper(r.customer_name)}</td>                  
                  <td>{r.client_ref || '-'}</td>
                  <td>{toUpper(getBlValue(r))}</td>
                  <td>{renderContainerCell(r)}</td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{toUpper(r.icd || '')}</span>
                    {editingIcdJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditIcd(r)} title="Edit ICD" />
                    )}
                    {editingIcdJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={icdOptions.map(i => ({ id: i.name, text: i.name }))}
                          value={selectedIcd}
                          onChange={(val) => setSelectedIcd(val)}
                          valueKey="id"
                          displayKey="text"
                          placeholder="Select ICD"
                          style={{ minWidth: 180 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyIcdChange(r, selectedIcd)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditIcd}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>                
                  <td>{renderDangerousClass(r)}</td>
                  { renderIcdNominationCell(r) }
                  <td style={{ textAlign: 'center' }}>
                    {isPreverification(r.assessment_status) ? (
                      <span style={{ color: 'red', fontWeight: 'bold' }}>PRE-V</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!r.duty_paid}
                        disabled={savingDutyFor === r.id || !hasPermission('MANIFEST REPORT', 'can_edit')} // 24. Disable checkbox
                        onChange={(e) => confirmSetDutyPaid(r, e.target.checked)}
                      />
                    )}
                  </td>                  
                  <td>{renderNotes(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {(sectionFilter === 'ALL' || sectionFilter === 'PENDING_ASSESSMENT') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2ff', borderRadius: 6, padding: 8, fontSize: 16 }}>PENDING ASSESSMENT</div>
          <div>
          <table className="jobs-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th>ID</th><th>IDF NO</th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span>
                  <br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th>ETA</th><th>CUSTOMER NAME</th><th>CLIENT REF</th><th>BL</th><th>CONTAINER SIZE</th><th>ICD</th><th>DANGEROUS CLASS</th><th>ICD NOMINATION</th><th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                                   const rows = applyFilter(pendingAssessment);
                  if (rows.length === 0) {
                    return <tr><td colSpan={12} className="no-data">No records found.</td></tr>;
                  }
                  const isAccepted = r => String(r.assessment_status || '').toUpperCase().includes('ACCEPT');
                  const acceptedFirst = rows.filter(isAccepted);
                  const others = rows.filter(r => !isAccepted(r));
                  const ordered = [...acceptedFirst, ...others];
                  return ordered.map(r => (
                    <tr
                      key={`pend-${r.id}`}
                      className={pendingManifestOutIds.has(r.id) ? 'manifest-out-pending-flash' : (isAccepted(r) ? 'pending-accepted-flash' : undefined)}
                      title={pendingManifestOutIds.has(r.id) ? 'MANIFEST IS OUT' : (isAccepted(r) ? 'ACCEPTED BUT NOT SENT' : undefined)}
                    >
                      <td>
                        <a href="#"
                          onClick={(e) => { e.preventDefault(); handleViewJob(r); }}>
                          {r.id}
                        </a>
                      </td>
                      <td>{r.idf_no || '-'}</td>
                      <td style={{ whiteSpace: 'pre-line' }}>
                        <span style={{ fontWeight: 'bold' }}>{toUpper(r.vessel)}</span>
                        <br />
                        <span style={{ fontWeight: 'normal' }}>
                          {toUpper(r.voyage_number)}
                        </span>
                        {editingVoyageJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                          <PenBtn onClick={() => startEditVoyage(r)} title="Edit vessel / voyage" />
                        )}
                        {editingVoyageJob === r.id && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Select2Dropdown
                              options={(
                                (editingVoyageJob === r.id ? editingVoyageOptions : voyagesForShippingLine(r.shipping_line_id)) || []
                              ).map(v => {
                                const vesselName = v.vessel_name || v.vessel || v.vesselName || '';
                                const matchingDetail = (v.voyage_details || []).find(d => {
                                  const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
                                  return slId !== null && String(slId) === String(r.shipping_line_id);
                                });
                                const voyageNo = matchingDetail?.voyage_number || v.voyage_number || v.voyage || v.voyage_no || v.voyageNumber || '';
                                const label = voyageNo ? `${vesselName} ${voyageNo}`.trim() : vesselName || (v.name || '');
                                return { id: v.id, text: label || String(v.id) };
                              })}
                              value={selectedVoyageId}
                              onChange={(val) => setSelectedVoyageId(val)}
                              valueKey="id"
                              displayKey="text"
                              noOptionsMessage="NO VESSEL VOYAGE FOR THE SELECTED SHIPPING LINE AVAILABLE"
                              placeholder="Select voyage"
                              style={{ minWidth: 220 }}
                              clearable={true}
                            />
                            <button className="icon-btn save-btn" title="Save" onClick={() => applyVoyageChange(r, selectedVoyageId)}>
                              <i className="fa fa-save" aria-hidden="true"></i>
                            </button>
                            <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditVoyage}>
                              <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                          </div>
                        )}
                      </td>
                      <td>{fmtDateShort(r.eta)}</td>
                      <td>{toUpper(r.customer_name)}</td>
                      <td>{r.client_ref || '-'}</td>
                      <td>{toUpper(getBlValue(r))}</td>
                      <td>{renderContainerCell(r)}</td>
                      <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{toUpper(r.icd || '')}</span>
                        {editingIcdJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                          <PenBtn onClick={() => startEditIcd(r)} title="Edit ICD" />
                        )}
                        {editingIcdJob === r.id && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Select2Dropdown
                              options={icdOptions.map(i => ({ id: i.name, text: i.name }))}
                              value={selectedIcd}
                              onChange={(val) => setSelectedIcd(val)}
                              valueKey="id"
                              displayKey="text"
                              placeholder="Select ICD"
                              style={{ minWidth: 180 }}
                            />
                            <button className="icon-btn save-btn" title="Save" onClick={() => applyIcdChange(r, selectedIcd)}>
                              <i className="fa fa-save" aria-hidden="true"></i>
                            </button>
                            <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditIcd}>
                              <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                          </div>
                        )}
                      </td>        
                      <td>{renderDangerousClass(r)}</td>
                      { renderIcdNominationCell(r) }
                      <td>{renderNotes(r)}</td>
                    </tr>
                  ));
                })()}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {(sectionFilter === 'ALL' || sectionFilter === 'PENDING_FILES') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, background: '#ffe0e0', borderRadius: 6, padding: 8, fontSize: 16 }}>PENDING FILES</div>
          <div>
          <table className="jobs-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th>ID</th><th>IDF NO</th>
                <th style={{ whiteSpace: 'pre-line' }}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span>
                  <br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th>ETA</th><th>CUSTOMER NAME</th><th>CLIENT REF</th><th>BL</th><th>CONTAINER SIZE</th><th>ICD</th><th>DANGEROUS CLASS</th><th>ICD NOMINATION</th><th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {applyFilter(pendingFiles).length === 0 ? (
                <tr><td colSpan={12} className="no-data">No records found.</td></tr>
              ) : applyFilter(pendingFiles).map(r => (
                <tr key={`pf-${r.id}`}>
                  <td>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(r); }}>
                      {r.id}
                    </a>
                  </td>
                  <td>{r.idf_no || '-'}</td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    <span style={{ fontWeight: 'bold' }}>{toUpper(r.vessel)}</span>
                    <br />
                    <span style={{ fontWeight: 'normal' }}>
                      {toUpper(r.voyage_number)}
                    </span>
                    {editingVoyageJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditVoyage(r)} title="Edit vessel / voyage" />
                    )}
                    {editingVoyageJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={(
                            (editingVoyageJob === r.id ? editingVoyageOptions : voyagesForShippingLine(r.shipping_line_id)) || []
                          ).map(v => {
                            const vesselName = v.vessel_name || v.vessel || v.vesselName || '';
                            const matchingDetail = (v.voyage_details || []).find(d => {
                              const slId = d.shipping_line?.id ?? d.shipping_line ?? null;
                              return slId !== null && String(slId) === String(r.shipping_line_id);
                            });
                            const voyageNo = matchingDetail?.voyage_number || v.voyage_number || v.voyage || v.voyage_no || v.voyageNumber || '';
                            const label = voyageNo ? `${vesselName} ${voyageNo}`.trim() : vesselName || (v.name || '');
                            return { id: v.id, text: label || String(v.id) };
                          })}
                          value={selectedVoyageId}
                          onChange={(val) => setSelectedVoyageId(val)}
                          valueKey="id"
                          displayKey="text"
                          noOptionsMessage="NO VESSEL VOYAGE FOR THE SELECTED SHIPPING LINE AVAILABLE"
                          placeholder="Select voyage"
                          style={{ minWidth: 220 }}
                          clearable={true}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyVoyageChange(r, selectedVoyageId)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditVoyage}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{fmtDateShort(r.eta)}</td>
                  <td>{toUpper(r.customer_name)}</td>
                  <td>{r.client_ref || '-'}</td>
                  <td>
                    {/* 22. Conditionally render BL as a link or plain text */
                    hasPermission('MANIFEST REPORT', 'can_edit') ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); handleBlClick(r); }} title={buildDatesTooltip(r)}>
                            {toUpper(getBlValue(r))}
                        </a>
                    ) : (
                        <span title={buildDatesTooltip(r)}>{toUpper(getBlValue(r))}</span>
                    )}
                  </td>
                  <td>{renderContainerCell(r)}</td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{toUpper(r.icd || '')}</span>
                    {/* 23. Conditionally render the edit ICD button */}
                    {editingIcdJob !== r.id && hasPermission('MANIFEST REPORT', 'can_edit') && (
                      <PenBtn onClick={() => startEditIcd(r)} title="Edit ICD" />
                    )}
                    {editingIcdJob === r.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select2Dropdown
                          options={icdOptions.map(i => ({ id: i.name, text: i.name }))}
                          value={selectedIcd}
                          onChange={(val) => setSelectedIcd(val)}
                          valueKey="id"
                          displayKey="text"
                          placeholder="Select ICD"
                          style={{ minWidth: 180 }}
                        />
                        <button className="icon-btn save-btn" title="Save" onClick={() => applyIcdChange(r, selectedIcd)}>
                          <i className="fa fa-save" aria-hidden="true"></i>
                        </button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditIcd}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{renderDangerousClass(r)}</td>
                  { renderIcdNominationCell(r) }
                  <td>{renderNotes(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {manifestDatesDialog.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 420 }}>
            <h3 style={{ marginTop: 0 }}>Manifest Dates (IDF: {manifestDatesDialog.idfNo})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                Discharge Date / Time
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="date"
                    value={manifestDatesDialog.dischargeDate || ''}
                    onChange={e => setManifestDatesDialog(d => ({ ...d, dischargeDate: e.target.value }))}
                    readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')} // 32. Make read-only
                    style={{ flex: 1 }}
                  />
                  <input
                    type="time"
                    value={manifestDatesDialog.dischargeTime || ''}
                    onChange={e => setManifestDatesDialog(d => ({ ...d, dischargeTime: e.target.value }))}
                    readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')}
                    style={{ width: 100 }}
                  />
                </div>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                Carry In End Date / Time
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="date"
                    value={manifestDatesDialog.carryInEndDate || ''}
                    onChange={e => setManifestDatesDialog(d => ({ ...d, carryInEndDate: e.target.value }))}
                    readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')} // 33. Make read-only
                    style={{ flex: 1 }}
                  />
                  <input
                    type="time"
                    value={manifestDatesDialog.carryInEndTime || ''}
                    onChange={e => setManifestDatesDialog(d => ({ ...d, carryInEndTime: e.target.value }))}
                    readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')}
                    style={{ width: 100 }}
                  />
                </div>
              </label>
              {(manifestDatesDialog.cargoType || '').toUpperCase() === 'LCL' && (
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                  Stripping Date
                  <input
                    type="date"
                    value={manifestDatesDialog.strippingDate || ''}
                    onChange={e => setManifestDatesDialog(d => ({ ...d, strippingDate: e.target.value }))}
                    readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')} // 34. Make read-only
                  />
                </label>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" className="cancel-btn" onClick={closeManifestDates}>Cancel</button>
              {/* 35. Conditionally render Save button */}
              {hasPermission('MANIFEST REPORT', 'can_edit') && (
                <button
                  type="button"
                  className="add-btn"
                  disabled={manifestDatesDialog.saving}
                  onClick={saveManifestDates}
                >
                  {manifestDatesDialog.saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {dutyPaidDialog.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 420 }}>
            <h3 style={{ marginTop: 0 }}>Duty Paid Date (IDF: {dutyPaidDialog.idfNo || '-'})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                Duty Paid Date
                <input
                  type="date"
                  value={dutyPaidDialog.dutyPaidDate || ''}
                  onChange={e => setDutyPaidDialog(d => ({ ...d, dutyPaidDate: e.target.value }))}
                />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" className="cancel-btn" onClick={() => { pendingHighlightIdRef.current = null; closeDutyPaidDateDialog(); }}>Cancel</button>
              <button
                type="button"
                className="add-btn"
                disabled={dutyPaidDialog.saving}
                onClick={saveDutyPaidDate}
              >
                {dutyPaidDialog.saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* confirm dialog modal */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />
      
      {/* ETB/ATB edit dialog */}
      {etbAtbDialog.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 360 }}>
            <h3 style={{ marginTop: 0 }}>Edit ETB / ATB</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {etbAtbDialog.ata && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                  ATA (Actual Time of Arrival): <strong>{new Date(etbAtbDialog.ata).toLocaleDateString('en-GB')}</strong>
                </div>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                ETB (Estimated Time of Berthing)
                <input
                  type="date"
                  value={etbAtbDialog.etb || ''}
                  min={etbAtbDialog.ata || undefined}
                  onChange={e => setEtbAtbDialog(d => ({ ...d, etb: e.target.value, error: '' }))}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                ATB (Actual Time of Berthing)
                {etbAtbDialog.dischargeDate && (
                  <span style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>
                    Must not be after Discharge Date ({new Date(etbAtbDialog.dischargeDate).toLocaleDateString('en-GB')})
                  </span>
                )}
                <input
                  type="date"
                  value={etbAtbDialog.atb || ''}
                  min={etbAtbDialog.ata || undefined}
                  max={etbAtbDialog.dischargeDate || undefined}
                  onChange={e => setEtbAtbDialog(d => ({ ...d, atb: e.target.value, error: '' }))}
                />
              </label>
            </div>
            {etbAtbDialog.error && (
              <div style={{ color: '#c0392b', fontSize: 12, marginTop: 8 }}>{etbAtbDialog.error}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" className="cancel-btn" onClick={closeEtbAtbDialog}>Cancel</button>
              <button
                type="button"
                className="add-btn"
                disabled={etbAtbDialog.saving}
                onClick={saveEtbAtb}
              >
                {etbAtbDialog.saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note dialog */}
      {noteDialogOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 420 }}>
            <h3 style={{ marginTop: 0 }}>Add Note (Show on Manifest)</h3>
            <div style={{ marginBottom: 8 }}>
              <label>Subject</label>
              <input
                value={noteSubject}
                onChange={e => setNoteSubject((e.target.value || '').toUpperCase())}
                style={{ width: '100%', padding: 6 }}
                readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')} // 36. Make read-only
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label>Details</label>
              <textarea
                value={noteDetails}
                onChange={e => setNoteDetails((e.target.value || '').toUpperCase())}
                rows={6}
                style={{ width: '100%', padding: 6 }}
                readOnly={!hasPermission('MANIFEST REPORT', 'can_edit')} // 37. Make read-only
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="cancel-btn" onClick={closeNoteDialog}>Cancel</button>
              {/* 38. Conditionally render Save button */}
              {hasPermission('MANIFEST REPORT', 'can_edit') && (
                <button type="button" className="add-btn" onClick={saveNote} disabled={savingNote}>
                  {savingNote ? 'Saving...' : 'Save Note'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                        &times;
                    </button>
                    <JobForm
                        mode={formMode}
                        initialData={currentJob}
                        onClose={handleFormClose}
                    />
                </div>
            </div>
        )}
    </div>
  );
}